import React, {useContext, useEffect, useRef, useState} from "react";
import {Alert, Button, Col, Form, FormFeedback, FormGroup, Input, Label, Row} from "reactstrap";
import Swal from "sweetalert2";
import ReCAPTCHA from "react-google-recaptcha";
import Constants from "../inc/Constants";
import LangContext from "../providers/LangContext";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faSpinner} from "@fortawesome/pro-light-svg-icons/faSpinner";
import InputTypes from "./customform/inputs/InputTypes";
import {get, post} from "../inc/Http";
import {isEmpty} from "../inc/Helpers";
import HeaderFooterHOC from "../HOC/HeaderFooterHOC";

const SiteBaseForm = ({
                          terms,
                          page,
                          siteID,
                      }) => {
    const {lang} = useContext(LangContext);
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const [formData, setFormData] = useState({
        gender: "m",
        gender_partner: "v",
        country: "nl",
        show_naw: false,
    });
    const [customFormData, setCustomFormData] = useState({});
    const [customForm, setCustomForm] = useState(null);
    const [files, setFiles] = useState({});
    const visibleStyle = {display: "block"};
    const recaptchaRef = useRef();

    useEffect(() => {
        setCustomFormData({...customFormData, lang: lang.getLocale()});
    }, [lang]);

    useEffect(() => {
        if (page.form_id) {
            get(`form/${page.form_id}`, {siteId: siteID})
                .then(response => {
                    setCustomForm(response.data);
                    setCustomFormData({
                        ...customFormData,
                        formId: page.form_id,
                    });
                    setFormData({...formData, ...{show_naw: response.data.show_naw === 1}});
                })
                .catch(err => {
                    console.error(err);
                });
        }
    }, [page.form_id]);

    const onInputChange = e => {
        setFormData({...formData, [e.target.name]: e.target.value});
    };

    const onCustomInputChange = (id, data) => {

        if (customFormData[id] && data?.options) {
            data.options = {...customFormData[id].options, ...data?.options};
        }

        setCustomFormData({...customFormData, [id]: data});
    };

    const onCustomCheckboxChange = (fieldId, optionName, optionId, value) => {
        if (!value) {
            setCustomFormData({
                ...customFormData,
                [fieldId]: {
                    [optionName]: [...customFormData[fieldId][optionName].filter(option => option !== optionId)]

                }
            });
            return;
        }
        let oldOptions = [];
        if (!!customFormData[fieldId]) {
            oldOptions = customFormData[fieldId][optionName];
        }
        setCustomFormData({
            ...customFormData,
            [fieldId]:
                {
                    [optionName]: [...oldOptions, optionId]
                }
        });
    }

    const onFileUpload = (id, file) => {
        setFiles({
            ...files,
            [id]: file
        });
    };

    const onRecaptchaChange = async value => {
        setFormData({...formData, recaptcha: value});
    };

    const submitForm = e => {
        e.preventDefault();
        setLoading(true);
        let finalFormData = new FormData();

        for (const [key, file] of Object.entries(files)) {
            if(file){
                finalFormData.append(`file${key}`, file);
            }
        }

        finalFormData.append("formData", JSON.stringify({...formData}));
        finalFormData.append("customFormData", JSON.stringify({...customFormData}));

        post(`${siteID}/form`, finalFormData)
            .then(function (response) {
                Swal.fire({
                    text: response.data,
                    icon: "success",
                    timer: 3000
                }).then(() => {
                    setFormData({});
                    setCustomFormData({
                        gender: "m",
                        gender_partner: "v",
                        country: "nl"
                    })
                    setFiles({});
                    recaptchaRef.current.reset();
                    window.location.href = '/';
                });
            })
            .catch(function (error) {
                setErrors(error.response.data?.errors || {});
                setLoading(false);
                if(!!recaptchaRef.current){
                    recaptchaRef.current.reset();
                }
            });
    };

    const customHtml = (customForm) => {
        if (customForm.pivot?.custom_html) {
            return <div className="mb-3" dangerouslySetInnerHTML={{__html: customForm.pivot.custom_html}}></div>;
        }

        if (customForm.custom_html) {
            return <div className="mb-3" dangerouslySetInnerHTML={{__html: customForm.custom_html}}></div>;
        }

        return false;
    }

    if (!customForm) {
        return <div>
            <p><FontAwesomeIcon icon={faSpinner} spin/> {lang.get('sites.form_loading')}</p>
        </div>
    }

    if (customForm && customForm.pivot?.display_until) {
        if (new Date(customForm.pivot.display_until) < new Date()) {
            return (
                <div>
                    <h3>{lang.get('sites.form_closed')}</h3>
                    <p><strong>{lang.get('sites.form_closed_for_new_entries')}.</strong></p>
                </div>
            )
        }

    }

    return (
        <Form onSubmit={submitForm} className="site-form">
            {/* Before main formfields */}
            {(customForm && customHtml(customForm)) && (
                customHtml(customForm)
            )}
            {!isEmpty(customForm) && <RenderCustomFields
                custom_field_groups={customForm.custom_field_groups.filter(cfg => cfg.before_content === 1)}
                onChange={onCustomInputChange}
                onCheckboxChange={onCustomCheckboxChange}
                onFileUpload={onFileUpload}
                errors={errors}
                customFormData={customFormData}
            />
            }
            {customForm?.show_naw === 1 && <div>
                <h3>{lang.get("sites.your_information").toUpperCase()}</h3>
                <div className="row mb-2">
                    <div className="col-md-4 align-self-center pr-md-0">
                        <Label check>{lang.get("sites.name")} *</Label>
                    </div>
                    <div className="col-md-8">
                        <div className="form-row">
                            <div className="col-md-2 pr-md-0">
                                <Input
                                    type="select"
                                    name="gender"
                                    onChange={onInputChange}
                                    value={formData.gender}
                                >
                                    <option value="m">
                                        {lang.get("sites.mr")}
                                    </option>
                                    <option value="v">
                                        {lang.get("sites.mrs")}
                                    </option>
                                </Input>
                            </div>
                            <div className="col-md-5 pr-md-0">
                                <Input
                                    onChange={onInputChange}
                                    invalid={!!errors?.first_name}
                                    type="text"
                                    name="first_name"
                                    className="form-control"
                                    placeholder={lang.get("sites.first_name")}
                                    value={formData?.first_name || ''}
                                />
                                <FormFeedback>{errors.first_name}</FormFeedback>
                            </div>
                            <div className="col-md-5">
                                <Input
                                    onChange={onInputChange}
                                    invalid={!!errors?.last_name}
                                    type="text"
                                    name="last_name"
                                    className="form-control"
                                    placeholder={lang.get("sites.last_name")}
                                    value={formData?.last_name || ''}
                                />
                                <FormFeedback>{errors?.last_name}</FormFeedback>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="row mb-2">
                    <div className="col-md-4 align-self-center pr-md-0">
                        <Label check>
                            {lang.get("sites.name_partner_optional")}
                        </Label>
                    </div>
                    <div className="col-md-8">
                        <div className="form-row">
                            <div className="col-md-2 pr-md-0">
                                <Input
                                    type="select"
                                    name="gender_partner"
                                    onChange={onInputChange}
                                    value={formData?.gender_partner}
                                >
                                    <option value="v">
                                        {lang.get("sites.mrs")}
                                    </option>
                                    <option value="m">
                                        {lang.get("sites.mr")}
                                    </option>
                                </Input>
                            </div>
                            <div className="col-md-5 pr-md-0">
                                <Input
                                    onChange={onInputChange}
                                    invalid={!!errors.first_name_partner}
                                    type="text"
                                    name="first_name_partner"
                                    className="form-control"
                                    placeholder={lang.get("sites.first_name")}
                                    value={formData?.first_name_partner || ''}
                                />
                                <FormFeedback>
                                    {errors.first_name_partner}
                                </FormFeedback>
                            </div>
                            <div className="col-md-5">
                                <Input
                                    onChange={onInputChange}
                                    invalid={!!errors.last_name_partner}
                                    type="text"
                                    name="last_name_partner"
                                    className="form-control"
                                    placeholder={lang.get("sites.last_name")}
                                    value={formData?.last_name_partner || ''}
                                />
                                <FormFeedback>
                                    {errors.last_name_partner}
                                </FormFeedback>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="row mb-2">
                    <div className="col-md-4 pr-md-0 align-self-center">
                        <Label check>{lang.get("sites.address")} *</Label>
                    </div>
                    <div className="col-md-8">
                        <div className="form-row">
                            <div className="col-md-7 pr-md-0">
                                <Input
                                    onChange={onInputChange}
                                    invalid={!!errors.streetname}
                                    type="text"
                                    className="form-control"
                                    placeholder={lang.get("sites.streetname")}
                                    name="streetname"
                                    value={formData?.streetname || ''}
                                />
                                <FormFeedback>{errors.streetname}</FormFeedback>
                            </div>
                            <div className="col-md-5">
                                <Input
                                    onChange={onInputChange}
                                    invalid={!!errors.streetnumber}
                                    type="text"
                                    className="form-control"
                                    placeholder={lang.get("sites.house_number")}
                                    name="streetnumber"
                                    value={formData?.streetnumber || ''}
                                />
                                <FormFeedback>{errors.streetnumber}</FormFeedback>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="row mb-2">
                    <div className="col-md-4 pr-md-0 align-self-center">
                        <Label check>
                            {lang.get("sites.zipcode")} / {lang.get("sites.city")} *
                        </Label>
                    </div>
                    <div className="col-md-8">
                        <div className="form-row">
                            <div className="col-md-5 pr-md-0">
                                <Input
                                    onChange={onInputChange}
                                    invalid={!!errors.zipcode}
                                    type="text"
                                    className="form-control"
                                    placeholder={lang.get("sites.zipcode")}
                                    name="zipcode"
                                    value={formData?.zipcode || ''}
                                />
                                <FormFeedback>{errors.zipcode}</FormFeedback>
                            </div>
                            <div className="col-md-7">
                                <Input
                                    onChange={onInputChange}
                                    invalid={!!errors.city}
                                    type="text"
                                    className="form-control"
                                    placeholder={lang.get("sites.city")}
                                    name="city"
                                    value={formData?.city || ''}
                                />
                                <FormFeedback>{errors.city || ''}</FormFeedback>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="row mb-2">
                    <div className="col-md-4 pr-md-0 align-self-center">
                        <Label check>{lang.get("sites.country")} *</Label>
                    </div>
                    <div className="col-md-8">
                        <Input
                            type="select"
                            name="country"
                            onChange={onInputChange}
                            value={formData?.country}
                        >
                            <option value="nl">
                                {lang.get("portal.country_nl")}
                            </option>
                            <option value="be">
                                {lang.get("portal.country_be")}
                            </option>
                            <option value="de">
                                {lang.get("portal.country_de")}
                            </option>
                            <option value="at">
                                {lang.get("portal.country_at")}
                            </option>
                            <option value="es">
                                {lang.get("portal.country_es")}
                            </option>
                            <option value="fr">
                                {lang.get("portal.country_fr")}
                            </option>
                        </Input>
                    </div>
                </div>

                <div className="row mb-2">
                    <div className="col-md-4 pr-md-0 align-self-center">
                        <Label check>{lang.get("sites.phone")} *</Label>
                    </div>
                    <div className="col-md-8">
                        <Input
                            invalid={!!errors.phone}
                            type="text"
                            placeholder={lang.get("sites.phone")}
                            name="phone"
                            onChange={onInputChange}
                            value={formData?.phone || ''}
                        />
                        <FormFeedback>{errors.phone}</FormFeedback>
                    </div>
                </div>

                <div className="row mb-2">
                    <div className="col-md-4 pr-md-0 align-self-center">
                        <Label check>{lang.get("sites.email")} *</Label>
                    </div>
                    <div className="col-md-8">
                        <Input
                            invalid={!!errors.email}
                            type="email"
                            placeholder={lang.get("sites.email")}
                            name="email"
                            onChange={onInputChange}
                            value={formData?.email || ''}
                        />
                        <FormFeedback>{errors.email}</FormFeedback>
                    </div>
                </div>
                <div className="row mb-2">
                    <div className="col-md-4 pr-md-0 align-self-center">
                        <Label check>{lang.get("sites.email_again")} *</Label>
                    </div>
                    <div className="col-md-8">
                        <Input
                            invalid={'email_confirmation' in errors}
                            type="email"
                            placeholder={lang.get("sites.email_again")}
                            name="email_confirmation"
                            onChange={onInputChange}
                            value={formData?.email_confirmation || ''}
                        />
                        <FormFeedback>{errors.email_confirmation}</FormFeedback>
                    </div>
                </div>
            </div>}
            {/* After main formfields */}
            {!isEmpty(customForm) && <RenderCustomFields
                custom_field_groups={customForm.custom_field_groups.filter(cfg => cfg.before_content === 0)}
                onChange={onCustomInputChange}
                onCheckboxChange={onCustomCheckboxChange}
                onFileUpload={onFileUpload}
                errors={errors}
                customFormData={customFormData}
                terms={terms}
            />
            }
            <div className="row">
                <FormGroup className="col-md-12 mt-2">
                    <ReCAPTCHA
                        ref={recaptchaRef}
                        sitekey={Constants.RECAPTHCA_KEY.siteKey}
                        onChange={onRecaptchaChange}
                        theme={"light"}
                    />{" "}
                    {!!errors.recaptcha && (
                        <div className="invalid-feedback" style={visibleStyle}>
                            {errors.recaptcha}
                        </div>
                    )}
                </FormGroup>
            </div>
            {Object.keys(errors).length > 0 && (
                <Alert color="danger">
                    {lang.get("sites.bidding_form_has_errors")}
                </Alert>
            )}
            <Row>
                <Col sm={6} className="text-left mb-3">
                    <Button type="submit" color="primary" onClick={submitForm}>
                        {loading && <FontAwesomeIcon icon={faSpinner} spin/>}{" "}
                        {lang.get("sites.send")}{" "}
                    </Button>
                </Col>
            </Row>
        </Form>
    );
};

export default HeaderFooterHOC(SiteBaseForm);


const checkCondition = (operator, logic_value, target_values = []) => {
    switch (operator) {
        case 'is_not_equal_to':

            if (target_values.some(val => val == logic_value)) {
                return true;
            }
            break;
        case 'is_equal_to':

            if (!target_values.some(val => val == logic_value)) {
                return true;
            }
            break;
        case 'is_greater_than':
            if (!target_values.length) {
                return true;
            }
            if (parseInt(target_values[0]) <= parseInt(logic_value)) {
                return true;
            }
            break;
        case 'is_less_than':
            if (!target_values.length) {
                return false;
            }

            if (parseInt(target_values[0]) >= parseInt(logic_value)) {
                return true;
            }
            break;
        case 'has_any_value':
            if (target_values.length > 0) {
                return false;
            }

            if (!target_values[0]) {
                return true;
            }

            if (target_values[0] == '' || target_values[0].length === 0) {
                return true;
            }
            break;
        case 'has_no_value':
            if (!target_values.length) {
                return false;
            }
            if (!target_values[0] && target_values[0] != '' || target_values[0].length > 0) {
                return true;
            }
            break;
        case 'contains':

            if (!target_values.some(val => val == logic_value)) {
                return true;
            }
            break;
    }
    return false;
}

const isDisabled = (conditional_logics, customFormData) => {

    if (conditional_logics.length === 0) {
        return false;
    }

    return conditional_logics.reduce((acc, conditional_logic) => {
        if (acc) {
            return true;
        }
        let target_values = [];
        if (customFormData[conditional_logic['field']]) {
            target_values = Object.values(customFormData[conditional_logic['field']]);
            if(target_values[0] && Array.isArray(target_values[0])){
                target_values = target_values.flat();
            }
        }


        return checkCondition(conditional_logic.operator, conditional_logic.value, target_values);
    }, false)

}

const RenderCustomFields = ({
                                custom_field_groups,
                                onChange,
                                onCheckboxChange,
                                onFileUpload,
                                errors,
                                customFormData,
                                terms
                            }) => {

    return custom_field_groups.map(field => {

        let value = null;

        if (customFormData[field.id]) {
            value = customFormData[field.id][field.name];
        }

        if (isDisabled(field.conditional_logics, customFormData)) {
            return '';
        }

        return <InputTypes
            key={`field-${field.name}`}
            customField={field}
            type={field.type}
            value={value}
            onChange={onChange}
            onCheckboxChange={onCheckboxChange}
            onUploadChange={onFileUpload}
            errors={errors}
            terms={terms}
        />;
    })
};
